import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ManufacturerRequest } from '@shop/types';
import { Plus } from 'lucide-react';
import { type SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { brandsQuery, manufacturersQuery } from '../lib/queries';
import { useCan } from '../lib/session';
import type { Address, Brand, Manufacturer } from '../lib/types';

export function BrandsPage() {
  const { t } = useTranslation();
  const canWrite = useCan('catalog.write');
  const brands = useQuery(brandsQuery);
  const manufacturers = useQuery(manufacturersQuery);
  const [brandOpen, setBrandOpen] = useState(false);
  const [manufacturer, setManufacturer] = useState<Manufacturer | 'new'>();
  const byId = useMemo(
    () => new Map((manufacturers.data ?? []).map((m) => [m.id, m])),
    [manufacturers.data],
  );

  const brandColumns = useMemo<Columns<Brand>>(
    () => [
      { id: 'name', header: t('common.name'), accessorFn: (b) => b.name },
      { id: 'slug', header: t('products.slug'), accessorFn: (b) => b.slug },
      {
        id: 'manufacturer',
        header: t('products.manufacturer'),
        accessorFn: (b) => (b.manufacturerId ? (byId.get(b.manufacturerId)?.name ?? '') : ''),
      },
      {
        id: 'active',
        header: t('common.status'),
        accessorFn: (b) => b.isActive,
        cell: ({ row }) =>
          row.original.isActive ? (
            <Badge variant="success">{t('common.active')}</Badge>
          ) : (
            <Badge variant="outline">{t('common.inactive')}</Badge>
          ),
      },
    ],
    [t, byId],
  );

  const manufacturerColumns = useMemo<Columns<Manufacturer>>(
    () => [
      { id: 'name', header: t('common.name'), accessorFn: (m) => m.name },
      {
        id: 'address',
        header: t('brands.address'),
        accessorFn: (m) =>
          m.address ? `${m.address.line1}, ${m.address.city}, ${m.address.country}` : '',
        cell: ({ row }) =>
          row.original.address ? (
            `${row.original.address.line1}, ${row.original.address.city}, ${row.original.address.country}`
          ) : (
            <Badge variant="warning">{t('brands.noAddress')}</Badge>
          ),
      },
      { id: 'email', header: t('brands.email'), accessorFn: (m) => m.email ?? '' },
    ],
    [t],
  );

  return (
    <>
      <PageHeader title={t('nav.brands')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader
            title={t('brands.title')}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setBrandOpen(true);
                  }}
                >
                  <Plus />
                  {t('brands.new')}
                </Button>
              ) : null
            }
          />
          <CardContent>
            <DataTable
              columns={brandColumns}
              data={brands.data ?? []}
              loading={brands.isPending}
              rowKey={(b) => b.id}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={t('brands.manufacturers')}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setManufacturer('new');
                  }}
                >
                  <Plus />
                  {t('brands.newManufacturer')}
                </Button>
              ) : null
            }
          />
          <CardContent>
            <DataTable
              columns={manufacturerColumns}
              data={manufacturers.data ?? []}
              loading={manufacturers.isPending}
              rowKey={(m) => m.id}
              onRowClick={canWrite ? setManufacturer : undefined}
            />
          </CardContent>
        </Card>
      </div>
      {brandOpen ? (
        <BrandDialog
          manufacturers={manufacturers.data ?? []}
          onClose={() => {
            setBrandOpen(false);
          }}
        />
      ) : null}
      {manufacturer ? (
        <ManufacturerDialog
          key={manufacturer === 'new' ? 'new' : manufacturer.id}
          existing={manufacturer === 'new' ? undefined : manufacturer}
          onClose={() => {
            setManufacturer(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function BrandDialog({
  manufacturers,
  onClose,
}: {
  manufacturers: Manufacturer[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [error, setError] = useState<string>();

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      await api('/catalog/brands', {
        method: 'POST',
        body: { name, manufacturerId: manufacturerId || null, isActive: true },
      });
      await queryClient.invalidateQueries({ queryKey: brandsQuery.queryKey });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog open onClose={onClose} title={t('brands.new')}>
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <Field label={t('common.name')}>
          {(id) => (
            <Input
              id={id}
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('products.manufacturer')}>
          {(id) => (
            <Select
              id={id}
              value={manufacturerId}
              onChange={(e) => {
                setManufacturerId(e.target.value);
              }}
            >
              <option value="">{t('common.none')}</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.create')}</Button>
        </div>
      </form>
    </Dialog>
  );
}

const emptyAddress = (): Address => ({ line1: '', city: '', postalCode: '', country: '' });

function AddressFields({
  value,
  onChange,
}: {
  value: Address;
  onChange: (address: Address) => void;
}) {
  const { t } = useTranslation();
  const fields = [
    ['line1', t('brands.line1')],
    ['city', t('brands.city')],
    ['postalCode', t('brands.postalCode')],
    ['country', t('brands.country')],
  ] as const;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map(([key, label]) => (
        <Field key={key} label={label}>
          {(id) => (
            <Input
              id={id}
              maxLength={key === 'country' ? 2 : undefined}
              value={value[key] ?? ''}
              onChange={(e) => {
                onChange({
                  ...value,
                  [key]: key === 'country' ? e.target.value.toUpperCase() : e.target.value,
                });
              }}
            />
          )}
        </Field>
      ))}
    </div>
  );
}

function cleanAddress(a: Address): Address | undefined {
  if (!a.line1.trim() && !a.city.trim()) return undefined;
  return {
    line1: a.line1.trim(),
    city: a.city.trim(),
    country: a.country.trim(),
    ...(a.postalCode?.trim() ? { postalCode: a.postalCode.trim() } : {}),
  };
}

function ManufacturerDialog({
  existing,
  onClose,
}: {
  existing?: Manufacturer;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [website, setWebsite] = useState(existing?.website ?? '');
  const [address, setAddress] = useState<Address>(existing?.address ?? emptyAddress());
  const [rpName, setRpName] = useState(existing?.euResponsiblePerson?.name ?? '');
  const [rpAddress, setRpAddress] = useState<Address>(
    existing?.euResponsiblePerson?.address ?? emptyAddress(),
  );
  const [error, setError] = useState<string>();

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const rpAddr = cleanAddress(rpAddress);
    const addr = cleanAddress(address);
    const body: ManufacturerRequest = {
      name: name.trim(),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(website.trim() ? { website: website.trim() } : {}),
      ...(addr ? { address: addr } : {}),
      ...(rpName.trim() && rpAddr
        ? { euResponsiblePerson: { name: rpName.trim(), address: rpAddr } }
        : {}),
    };
    try {
      await api(existing ? `/catalog/manufacturers/${existing.id}` : '/catalog/manufacturers', {
        method: existing ? 'PATCH' : 'POST',
        body,
      });
      await queryClient.invalidateQueries({ queryKey: manufacturersQuery.queryKey });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={existing ? existing.name : t('brands.newManufacturer')}
      className="max-w-2xl"
    >
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('common.name')}>
            {(id) => (
              <Input
                id={id}
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('brands.email')}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('brands.website')}>
            {(id) => (
              <Input
                id={id}
                type="url"
                value={website}
                onChange={(e) => {
                  setWebsite(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t('brands.address')}</legend>
          <AddressFields value={address} onChange={setAddress} />
        </fieldset>
        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t('brands.euRp')}</legend>
          <p className="text-xs text-muted-foreground">{t('brands.euRpHint')}</p>
          <Field label={t('common.name')}>
            {(id) => (
              <Input
                id={id}
                value={rpName}
                onChange={(e) => {
                  setRpName(e.target.value);
                }}
              />
            )}
          </Field>
          <AddressFields value={rpAddress} onChange={setRpAddress} />
        </fieldset>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.save')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
